import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { ResourceCard } from '@/components/common/Card/ResourceCard';
import { Section } from '@/components/layout/Section';

import type { ResourceCardProps } from '@/components/common/Card/ResourceCard';
import type { BlockWithAudience } from '@/types/shared';

import type { Audience as SanityAudience, FeaturedGrid as SanityFeaturedGrid } from '@/sanity/sanity.types'

export interface FeaturedGridProps
  extends Omit<SanityFeaturedGrid, 'spotlight' | 'secondaryCards'>,
  BlockWithAudience {
  secondaryCards?: Array<
    Omit<ResourceCardProps, 'audienceRef'> & {
      audienceRef?: SanityAudience;
    }
  >;
  spotlight?: NewSpotlightProps;
}

interface NewSpotlightProps extends Omit<SpotlightProps, 'resourceCard'> {
  resourceCard?: Omit<ResourceCardProps, 'audienceRef'> & {
    audienceRef?: SanityAudience;
  };
}

interface SpotlightProps {
  _type: 'spotlight';
  resourceCard?: ResourceCardProps;
}

const FeaturedGrid = ({
  spotlight,
  secondaryCards,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
  pageAudience,
  isAudienceSwitcherEnabled,
}: FeaturedGridProps) => {
  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColour?.token}
      className="featured-grid"
    >
      <FeaturedGridContainer>
        <SpotlightContainer>
          <ResourceCard
            {...spotlight.resourceCard}
            pageAudience={pageAudience}
            isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
          />
        </SpotlightContainer>
        <SecondaryCardsContainer>
          {secondaryCards.map((card) => (
            <ResourceCard
              {...card}
              key={card._key}
              pageAudience={pageAudience}
              isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
            />
          ))}
        </SecondaryCardsContainer>
      </FeaturedGridContainer>
    </Section>
  );
};

const FeaturedGridContainer = styled.div`
  article {
    margin-bottom: var(--spacing-small);
  }

  ${screen.sm} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-300);

    article {
      margin-bottom: 0;
    }
  }
  ${screen.md} {
    grid-template-columns: 2fr 1fr;
  }
`;

const SpotlightContainer = styled.div`
  article {
    height: 100%;
  }
`;
const SecondaryCardsContainer = styled.div`
  ${screen.sm} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    article {
      height: fit-content;
      display: block;
    }
    article:first-of-type {
      margin-bottom: var(--spacing-small);
    }
  }
`;

export { FeaturedGrid };
